import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Zap, X, HelpCircle, Loader2, Minus, Plus, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import DhanConnectModal from "@/components/DhanConnectModal";

export const DHAN_REFERRAL_URL = "https://join.dhan.co/?invite=EVMCD04986";

/** 1-click BUY button for the suggested strike at the suggested limit price.
 *  Sits on the premium-plan card (where the actual entry prices are shown). */
export default function BuyViaDhan({ symbol, strike, optType, expiry, suggestedPrice, slPrice = 0, label }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [lots, setLots] = useState(1);
  const [orderType, setOrderType] = useState(suggestedPrice > 0 ? "LIMIT" : "MARKET");
  const [price, setPrice] = useState(suggestedPrice || 0);
  const [withSl, setWithSl] = useState(slPrice > 0);
  const [placing, setPlacing] = useState(false);
  const [result, setResult] = useState(null);

  const openModal = async () => {
    setResult(null);
    setPrice(suggestedPrice || 0);
    setOrderType(suggestedPrice > 0 ? "LIMIT" : "MARKET");
    setWithSl(slPrice > 0);
    setOpen(true);
    try {
      const { data } = await api.get("/dhan/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  };

  const place = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/dhan/place-single", {
        symbol, strike, opt_type: optType, expiry: expiry || "",
        qty_lots: lots, order_type: orderType,
        price: orderType === "LIMIT" ? Number(price) : 0,
        sl_trigger: withSl && slPrice > 0 ? Number(slPrice) : 0, confirm: true,
      });
      setResult(data);
    } catch (e) {
      setResult({ ok: false, detail: e?.response?.data?.detail || "Order failed" });
    } finally {
      setPlacing(false);
    }
  };

  if (!strike || !optType) return null;
  return (
    <>
      <div className="flex items-center gap-1.5 pt-1">
        <button
          onClick={openModal}
          className="inline-flex items-center gap-1.5 bg-[#22C55E] hover:bg-[#16A34A] text-black text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 transition-colors"
          data-testid="buy-via-dhan-btn"
        >
          <Zap className="h-3 w-3" /> {label || `Buy ${strike} ${optType} via DHAN`}
        </button>
        <Link to="/dhan-guide" title="How to connect DHAN" className="text-zinc-500 hover:text-[#3B9BFF] p-1" data-testid="dhan-guide-link-icon">
          <HelpCircle className="h-3.5 w-3.5" />
        </Link>
      </div>

      {open && (
        <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="buy-single-modal">
          <div className="bg-[#0a0a0a] border border-zinc-800 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#22C55E] font-mono">Live · DHAN execution</div>
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">
                  Buy {symbol} {strike} {optType}
                </h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" data-testid="buy-single-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {status === null && (
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 py-4 text-center">◢ Checking DHAN connection…</div>
              )}

              {status && !status.connected && !result && (
                <div className="space-y-3" data-testid="buy-single-not-connected">
                  <div className="text-sm text-zinc-300 leading-relaxed">
                    {status.stale_token
                      ? "Your DHAN token has expired — reconnect with a fresh token to place this order."
                      : "Connect your DHAN account to execute this trade in one click."}
                  </div>
                  <button
                    onClick={() => { setOpen(false); setConnectOpen(true); }}
                    className="w-full text-xs uppercase tracking-widest border border-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 text-[#22C55E] py-2.5 font-mono font-bold"
                    data-testid="buy-single-connect-btn"
                  >
                    Connect DHAN account
                  </button>
                  <a
                    href={DHAN_REFERRAL_URL} target="_blank" rel="noreferrer"
                    className="w-full inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-widest border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white py-2.5 font-mono"
                    data-testid="buy-single-open-account"
                  >
                    <ExternalLink className="h-3 w-3" /> Don&apos;t have DHAN? Open a free account
                  </a>
                  <Link to="/dhan-guide" className="block text-center text-[10px] text-[#3B9BFF] hover:underline font-mono uppercase tracking-widest" data-testid="buy-single-guide-link">
                    Step-by-step: How to connect →
                  </Link>
                </div>
              )}

              {status?.connected && !result && (
                <div className="space-y-4" data-testid="buy-single-form">
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                    <div className="border border-zinc-800 bg-[#121212] p-2">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">Contract</div>
                      <div className="text-white font-bold">{strike} {optType}{expiry ? ` · ${expiry}` : ""}</div>
                    </div>
                    <div className="border border-zinc-800 bg-[#121212] p-2">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">DHAN Client</div>
                      <div className="text-white font-bold">{status.client_id}</div>
                    </div>
                  </div>

                  <div className="flex border border-zinc-700">
                    {["LIMIT", "MARKET"].map((t) => (
                      <button key={t} onClick={() => setOrderType(t)} data-testid={`buy-single-type-${t.toLowerCase()}`}
                              className={`flex-1 py-1.5 text-[10px] uppercase tracking-widest font-mono font-bold ${orderType === t ? "bg-[#007AFF] text-white" : "text-zinc-400 hover:text-white"}`}>
                        {t}{t === "LIMIT" && suggestedPrice > 0 ? " (suggested)" : ""}
                      </button>
                    ))}
                  </div>

                  {orderType === "LIMIT" && (
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Limit price (₹) — suggested entry ₹{suggestedPrice}</label>
                      <input
                        type="number" step="0.05" value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        className="mt-1 w-full bg-[#0a0a0a] border border-zinc-800 focus:border-[#007AFF] px-3 py-2 text-sm font-mono outline-none"
                        data-testid="buy-single-price-input"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Lots</label>
                    <div className="mt-1 flex items-center gap-3">
                      <button onClick={() => setLots(Math.max(1, lots - 1))} className="border border-zinc-700 hover:border-zinc-500 p-1.5" data-testid="buy-single-lots-minus"><Minus className="h-3 w-3" /></button>
                      <span className="font-mono font-bold text-lg w-8 text-center" data-testid="buy-single-lots">{lots}</span>
                      <button onClick={() => setLots(Math.min(100, lots + 1))} className="border border-zinc-700 hover:border-zinc-500 p-1.5" data-testid="buy-single-lots-plus"><Plus className="h-3 w-3" /></button>
                      <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Qty auto-set from DHAN lot size</span>
                    </div>
                  </div>

                  {slPrice > 0 && (
                    <label className="flex items-center gap-2 border border-zinc-800 bg-[#121212] px-3 py-2 cursor-pointer" data-testid="buy-single-sl-toggle">
                      <input type="checkbox" checked={withSl} onChange={(e) => setWithSl(e.target.checked)} className="accent-[#EF4444]" data-testid="buy-single-sl-checkbox" />
                      <span className="text-[11px] font-mono text-zinc-300">Also place stop-loss <span className="text-[#EF4444] font-bold">SELL SL @ ₹{Number(slPrice).toFixed(2)}</span><span className="block text-[9px] text-zinc-500 uppercase tracking-widest">placed only after the BUY fills</span></span>
                    </label>
                  )}

                  <div className="text-[10px] text-zinc-500 leading-relaxed font-mono uppercase tracking-widest border-l-2 border-zinc-800 pl-3">
                    BUY only · intraday (MIS) · routed to your own DHAN account · SEBI: derivatives involve risk
                  </div>

                  <button
                    onClick={place} disabled={placing || (orderType === "LIMIT" && !(Number(price) > 0))}
                    className="w-full text-xs uppercase tracking-widest bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-50 text-black py-3 font-mono font-bold flex items-center justify-center gap-2"
                    data-testid="buy-single-confirm"
                  >
                    {placing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                    {placing ? "Placing order…" : `Confirm BUY · ${lots} lot${lots > 1 ? "s" : ""} ${orderType === "LIMIT" ? `@ ₹${price}` : "@ market"}`}
                  </button>
                </div>
              )}

              {result && (
                <div className="space-y-3" data-testid="buy-single-result">
                  <div className={`border p-3 flex items-start gap-2 text-sm ${result.ok ? "border-[#22C55E]/50 bg-[#22C55E]/10 text-[#22C55E]" : "border-[#EF4444]/50 bg-[#EF4444]/10 text-[#EF4444]"}`}>
                    {result.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
                    <div className="leading-relaxed">
                      {result.ok
                        ? <>Order placed · {result.trading_symbol || `${strike} ${optType}`} · qty {result.qty_units} ({lots} lot{lots > 1 ? "s" : ""} × {result.lot_size}) · ID {result.order_id}</>
                        : (result.detail || "Order failed")}
                    </div>
                  </div>
                  {result.sl_order && (
                    <div className={`border p-3 text-xs font-mono ${result.sl_order.ok ? "border-[#EF4444]/50 bg-[#EF4444]/10 text-[#EF4444]" : "border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]"}`} data-testid="buy-single-sl-result">
                      {result.sl_order.ok
                        ? <>Stop-loss placed · SELL SL trigger ₹{result.sl_order.trigger} · ID {result.sl_order.order_id}</>
                        : <>SL not placed: {result.sl_order.detail}</>}
                    </div>
                  )}
                  <button onClick={() => setOpen(false)} className="w-full text-xs uppercase tracking-widest border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white py-2.5 font-mono" data-testid="buy-single-done">
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <DhanConnectModal open={connectOpen} onClose={() => setConnectOpen(false)} onConnected={() => { setConnectOpen(false); openModal(); }} />
    </>
  );
}
